'use client'
import { ChevronDownIcon, XMarkIcon, CheckIcon } from '@heroicons/react/20/solid'
import { useState, useMemo, useRef, useEffect } from 'react'

/**
 * MultiSelectCombobox - A reusable multi-select dropdown with search
 * Dropdown stays open until user clicks outside
 * 
 * @param {string} label - Label for the combobox
 * @param {Array} options - Array of option objects with { value, label } or array of strings
 * @param {Array} selectedValues - Array of selected values
 * @param {Function} onChange - Callback when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {string} icon - Optional icon component
 * @param {boolean} disabled - Whether the combobox is disabled
 */
export default function MultiSelectCombobox({ 
  label, 
  options = [], 
  selectedValues = [], 
  onChange, 
  placeholder = 'Select options...',
  icon: Icon,
  disabled = false
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Normalize options to always have { value, label } format
  const normalizedOptions = useMemo(() => {
    return options.map(opt => 
      typeof opt === 'string' 
        ? { value: opt, label: opt.charAt(0).toUpperCase() + opt.slice(1) }
        : opt
    )
  }, [options])

  // Filter options based on query
  const filteredOptions = useMemo(() => {
    if (query === '') return normalizedOptions
    
    return normalizedOptions.filter((option) => {
      return option.label.toLowerCase().includes(query.toLowerCase())
    })
  }, [query, normalizedOptions])

  // Get selected option objects
  const selectedOptions = useMemo(() => {
    return normalizedOptions.filter(opt => selectedValues.includes(opt.value))
  }, [normalizedOptions, selectedValues])

  // Handle selection toggle
  const handleToggle = (option, e) => {
    e.stopPropagation()
    const isSelected = selectedValues.includes(option.value)
    
    if (isSelected) {
      // Remove from selection
      onChange(selectedValues.filter(v => v !== option.value))
    } else {
      // Add to selection
      onChange([...selectedValues, option.value])
    }
    // Keep dropdown open - don't close
  }

  // Remove a selected item
  const handleRemove = (value, e) => {
    e.stopPropagation()
    onChange(selectedValues.filter(v => v !== value))
  }

  // Clear all selections
  const handleClearAll = (e) => {
    e.stopPropagation()
    onChange([])
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
        setQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          <span className="flex items-center gap-1">
            {Icon && <Icon className="h-3.5 w-3.5 text-gray-400" />}
            {label}
          </span>
        </label>
      )}
      
      <div className="relative">
        {/* Display selected items as pills */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`
            block w-full rounded-md bg-white dark:bg-gray-700 
            border border-gray-300 dark:border-gray-600
            min-h-[38px] py-1.5 pr-10 pl-3
            text-left
            ${isOpen ? 'ring-2 ring-purple-500 border-purple-500' : ''}
            transition-all
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500'}
          `}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1.5 items-center">
            {selectedOptions.length > 0 ? (
              <>
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                  >
                    {option.label}
                    <button
                      type="button"
                      onClick={(e) => handleRemove(option.value, e)}
                      className="inline-flex items-center justify-center hover:bg-purple-200 dark:hover:bg-purple-800/50 rounded-sm transition-colors"
                      disabled={disabled}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
                {selectedOptions.length > 1 && (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition-colors"
                    disabled={disabled}
                  >
                    Clear all
                  </button>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500">
                {placeholder}
              </span>
            )}
          </div>
        </button>

        {/* Dropdown button */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDownIcon 
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            aria-hidden="true" 
          />
        </div>

        {/* Dropdown options */}
        {isOpen && (
          <div
            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {/* Search input when dropdown is open */}
            {normalizedOptions.length > 5 && (
              <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-2 py-2 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  onClick={(e) => e.stopPropagation()}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 py-1.5 px-3 text-sm focus:border-purple-500 focus:ring-purple-500 focus:ring-1"
                />
              </div>
            )}

            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No options found
              </div>
            ) : (
              <>
                {/* Select All / Deselect All */}
                {filteredOptions.length > 1 && (
                  <div className="px-2 py-1 border-b border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (selectedValues.length === normalizedOptions.length) {
                          onChange([])
                        } else {
                          onChange(normalizedOptions.map(opt => opt.value))
                        }
                      }}
                      className="w-full text-left px-2 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                    >
                      {selectedValues.length === normalizedOptions.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                )}

                {filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value)
                  
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => handleToggle(option, e)}
                      className="w-full cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-left"
                    >
                      <div className="flex items-center">
                        <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                          {option.label}
                        </span>
                      </div>

                      {isSelected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-purple-600 dark:text-purple-400">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </button>
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
